import React, { useCallback, useEffect, useState } from "react";
import { Autocomplete, TextField } from "@mui/material";
import {
  getPurchaseRequestProjects,
  getPurchaseRemarks,
  addPurchaseRemark,
} from "../../api/service";

export const AGEING = [
  { value: "", label: "Any age" },
  { value: "0-2", label: "0-2 days" },
  { value: "3-7", label: "3-7 days" },
  { value: "8-14", label: "8-14 days" },
  { value: "15+", label: "15+ days" },
];

export function fmtMoney(v) {
  if (v === null || v === undefined || v === "") return "-";
  const n = Number(v);
  if (Number.isNaN(n)) return "-";
  return n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function fmtQty(v) {
  if (v === null || v === undefined || v === "") return "-";
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return n.toLocaleString("en-IN", { maximumFractionDigits: 2 });
}

export function AgeingSelect({ value, onChange }) {
  return (
    <select
      className="form-select form-select-sm"
      value={value}
      onChange={(e) => onChange(e.target.value)}
    >
      {AGEING.map((a) => (
        <option key={a.value} value={a.value}>{a.label}</option>
      ))}
    </select>
  );
}

export function ProjectFilter({ value, onChange }) {
  const [options, setOptions] = useState([]);

  useEffect(() => {
    let live = true;
    getPurchaseRequestProjects()
      .then((res) => {
        if (live) setOptions(res?.results || []);
      })
      .catch(() => {});
    return () => {
      live = false;
    };
  }, []);

  return (
    <Autocomplete
      size="small"
      style={{ minWidth: 240 }}
      options={options}
      value={value}
      getOptionLabel={(o) => o?.display || ""}
      isOptionEqualToValue={(o, v) => o?.id === v?.id}
      onChange={(_, v) => onChange(v)}
      noOptionsText="No projects"
      renderInput={(params) => <TextField {...params} placeholder="All projects" />}
    />
  );
}

export function RemarksPanel({ requestId }) {
  const [items, setItems] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = useCallback(() => {
    return getPurchaseRemarks(requestId)
      .then((res) => {
        setItems(res?.results || []);
        setErr("");
      })
      .catch((e) => setErr(e.message || "Could not load remarks."));
  }, [requestId]);

  useEffect(() => {
    load();
  }, [load]);

  const post = async () => {
    const t = text.trim();
    if (!t) return;
    setBusy(true);
    try {
      await addPurchaseRemark(requestId, { text: t });
      setText("");
      await load();
    } catch (e) {
      setErr(e.message || "Could not post remark.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card mt-3">
      <div className="card-header py-2">
        <strong>Remarks</strong> <span className="text-muted small">({items.length})</span>
      </div>
      <div className="card-body py-2">
        {err && <div className="alert alert-danger py-1">{err}</div>}
        <div style={{ maxHeight: 220, overflowY: "auto" }}>
          {items.length === 0 && <div className="text-muted small">No remarks yet.</div>}
          {items.map((r) => (
            <div key={r.id} className="border-bottom py-1">
              <div className="small text-muted">
                {r.author ? r.author.username : "-"} {"\u00B7"} {new Date(r.created_at).toLocaleString()}
              </div>
              <div style={{ whiteSpace: "pre-wrap" }}>{r.text}</div>
            </div>
          ))}
        </div>
        <div className="d-flex gap-2 mt-2">
          <textarea
            className="form-control form-control-sm"
            rows={2}
            maxLength={2000}
            placeholder="Add a remark..."
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
          <button
            type="button"
            className="btn btn-outline-primary btn-sm"
            disabled={busy || !text.trim()}
            onClick={post}
          >
            Add
          </button>
        </div>
        <div className="small text-muted mt-1">Remarks are permanent and cannot be edited.</div>
      </div>
    </div>
  );
}
